import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/fetcher';
import { ApiEndpoint } from '@/api/registry';
import type { ReplenishmentSummaryResponse } from '@/features/decisions/decisions.api.types';
import { useTenant } from '@/providers/TenantProvider';

export interface UseReplenishmentSummaryParams {
  scenarioRunId: string;
  runId: string;
  refExperimentId: string;
  experimentId: string;
  period: string;
  metric?: 'reorder_qty' | 'reorder_point';
}

// Wraps GET /replenishment/summary. Returns Δ-to-Plan counts for the KPI cards.
export function useReplenishmentSummary({
  scenarioRunId,
  runId,
  refExperimentId,
  experimentId,
  period,
  metric = 'reorder_qty',
}: UseReplenishmentSummaryParams) {
  const { tenantAlias } = useTenant();

  const query = useQuery<ReplenishmentSummaryResponse, Error>({
    queryKey: ['rep-summary', tenantAlias, scenarioRunId, runId, refExperimentId, experimentId, period, metric],
    enabled: Boolean(scenarioRunId && runId && refExperimentId && experimentId && period),
    retry: false,
    staleTime: 30_000,
    queryFn: () =>
      apiFetch<ReplenishmentSummaryResponse>(tenantAlias, ApiEndpoint.SimulationReplenishmentSummary, {
        pathParams: { scenarioRunId, runId },
        query: {
          ref_experiment_id: refExperimentId,
          experiment_id: experimentId,
          period,
          metric,
        },
      }),
  });

  return {
    summary: query.data?.summary,
    resolved: query.data?.resolved,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}
